import store from './actions/store';
import { LOGOUT, LOGIN } from './actions/types';
import { setHiddenItemsId } from "./actions/sidebarActions";
import axios from 'axios';

const SESSION_KEY = 'marketplace_session';
const IMPERSONATION_BACKUP_KEY = 'marketplace_impersonation_backup';
const INACTIVITY_LIMIT = 2 * 60 * 60 * 1000; // 2 horas en milisegundos

class Auth {
  constructor() {
    this.authenticated = false;
  }

  isAuthenticated = () => {
    const isAuth = store.getState().general.isAuthenticated || false;
    if (isAuth) return true;

    // Fallback para evitar rebote a login en recargas/race conditions
    const hasSession = !!window.sessionStorage.getItem(SESSION_KEY);
    const hasToken = !!window.localStorage.getItem("token");
    return hasSession && hasToken;
  }

  buildSessionData(idSeller, userName, tipoUsuario, userId, hiddenItemsIds, codProv, impersonation = null, canImpersonate = false) {
    return {
      idSeller,
      userName,
      tipoUsuario,
      userId,
      codProv,
      hiddenItemsIds: Array.isArray(hiddenItemsIds) ? hiddenItemsIds : [],
      impersonation: impersonation || null,
      canImpersonate: !!canImpersonate
    };
  }

  resolveUserName(data) {
    if (data?.userName) return data.userName;

    try {
      const token = window.localStorage.getItem("token");
      if (!token) return "";

      const payload = token.replace(/^Bearer\s+/i, "").split(".")[1];
      if (!payload) return "";

      const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
      return decoded.sub || decoded.email || "";
    } catch (e) {
      return "";
    }
  }

  applyLoginState(data) {
    const payload = {
      idSeller: data?.idSeller ?? null,
      userName: this.resolveUserName(data),
      tipoUsuario: data?.tipoUsuario ?? 0,
      userId: data?.userId ?? 0,
      codProv: data?.codProv ?? null,
      impersonation: data?.impersonation || null,
      canImpersonate: !!data?.canImpersonate
    };

    store.dispatch({
      type: LOGIN,
      payload
    });

    store.dispatch(setHiddenItemsId(Array.isArray(data?.hiddenItemsIds) ? data.hiddenItemsIds : []));
  }

  login(idSeller, userName, tipoUsuario, userId, hiddenItemsIds, codProv, impersonation = null, canImpersonate = false) {
    const sessionData = this.buildSessionData(
      idSeller,
      userName,
      tipoUsuario,
      userId,
      hiddenItemsIds,
      codProv,
      impersonation,
      canImpersonate
    );

    this.applyLoginState(sessionData);
    this.saveSession(sessionData);
  }

  loginWithSession(sessionData) {
    this.applyLoginState(sessionData);
    this.saveSession(sessionData);
  }

  setAuthToken(token) {
    const authToken = token?.startsWith?.('Bearer ') ? token : `Bearer ${token}`;
    window.localStorage.setItem("token", authToken);
    axios.defaults.headers.common['Authorization'] = authToken;
  }

  startImpersonation(sessionData, token) {
    const currentSession = window.sessionStorage.getItem(SESSION_KEY);
    const currentToken = window.localStorage.getItem("token");
    const hasBackup = !!window.sessionStorage.getItem(IMPERSONATION_BACKUP_KEY);

    if (!hasBackup && currentSession && currentToken) {
      try {
        window.sessionStorage.setItem(
          IMPERSONATION_BACKUP_KEY,
          JSON.stringify({
            session: JSON.parse(currentSession),
            token: currentToken
          })
        );
      } catch (e) {
        console.error("Error saving impersonation backup:", e);
      }
    }

    if (token) {
      this.setAuthToken(token);
    }

    this.loginWithSession(sessionData);
  }

  stopImpersonation() {
    const backupRaw = window.sessionStorage.getItem(IMPERSONATION_BACKUP_KEY);
    if (!backupRaw) {
      this.logout();
      return false;
    }

    try {
      const backup = JSON.parse(backupRaw);
      if (!backup?.session || !backup?.token) {
        this.logout();
        return false;
      }

      this.setAuthToken(backup.token);
      this.applyLoginState(backup.session);
      this.saveSession(backup.session);
      window.sessionStorage.removeItem(IMPERSONATION_BACKUP_KEY);
      return true;
    } catch (e) {
      console.error("Error restoring impersonation session:", e);
      this.logout();
      return false;
    }
  }

  isImpersonating() {
    try {
      const session = window.sessionStorage.getItem(SESSION_KEY);
      if (!session) return false;
      const data = JSON.parse(session);
      return !!data?.impersonation?.isActive;
    } catch (e) {
      return false;
    }
  }

  logout() {
    this.clearSession();
    store.dispatch({
      type: LOGOUT,
      payload: false,
    });
  }

  saveSession(data) {
    const sessionData = {
      ...data,
      lastActivity: Date.now()
    };
    try {
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
    } catch (e) {
      console.error("Error saving to sessionStorage:", e);
    }
  }

  clearSession() {
    window.sessionStorage.removeItem(SESSION_KEY);
    window.sessionStorage.removeItem(IMPERSONATION_BACKUP_KEY);
    window.localStorage.removeItem("token");
    delete axios.defaults.headers.common['Authorization'];
  }

  updateActivity() {
    const session = window.sessionStorage.getItem(SESSION_KEY);
    if (session) {
      const data = JSON.parse(session);
      data.lastActivity = Date.now();
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
    }
  }

  // Combina la lógica de initSession y checkSession
  checkSession() {
    const session = window.sessionStorage.getItem(SESSION_KEY);
    const token = window.localStorage.getItem("token");

    if (!session || !token) {
      // If we have no session/token, but we thought we were logged in? 
      // Por ahora solo retorna, el estado de la aplicación generalmente comienza como no autenticado.
      return;
    }

    try {
      const data = JSON.parse(session);
      const now = Date.now();

      if (now - data.lastActivity > INACTIVITY_LIMIT) {
        this.logout();
      } else {
        // Restore Axios Header
        axios.defaults.headers.common['Authorization'] = token;

        this.applyLoginState(data);

        this.updateActivity();
      }
    } catch (e) {
      console.error("Error restoring session", e);
      this.logout();
    }

  }
}

export default new Auth();
